import { Controller } from "@hotwired/stimulus"

// Interactive steward availability editor. Drag-select a range of days on the
// month grid, then Open (with price / min-max stay), Block, or Clear it — each
// action POSTs to /properties/:id/set_availability and the grid re-fetches the
// authoritative state. Replaces the blind date-field rows as the primary tool.
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const MAX_FUTURE_MONTHS = 24

export default class extends Controller {
  static targets = ["grid", "monthLabel", "prev", "next", "summary", "price", "minStay", "maxStay", "capacity", "dow"]
  static values = {
    url: String,         // calendar JSON
    applyUrl: String,    // set_availability
    months: { type: Number, default: 2 },
    area: { type: Boolean, default: false },
  }

  connect() {
    this.days = {}
    this.fetched = new Set()
    this.selStart = null
    this.selEnd = null
    this.dragging = false
    this.viewMonth = this.#startOfMonth(new Date())

    this.gridTarget.addEventListener("mousedown", this.#onDown)
    this.gridTarget.addEventListener("mouseover", this.#onOver)
    document.addEventListener("mouseup", this.#onUp)

    this.#loadVisible().then(() => this.render())
  }

  disconnect() {
    this.gridTarget.removeEventListener("mousedown", this.#onDown)
    this.gridTarget.removeEventListener("mouseover", this.#onOver)
    document.removeEventListener("mouseup", this.#onUp)
  }

  prev() {
    if (this.viewMonth <= this.#startOfMonth(new Date())) return
    this.viewMonth = this.#addMonths(this.viewMonth, -1)
    this.#loadVisible().then(() => this.render())
  }

  next() {
    const cap = this.#addMonths(this.#startOfMonth(new Date()), MAX_FUTURE_MONTHS)
    if (this.viewMonth >= cap) return
    this.viewMonth = this.#addMonths(this.viewMonth, 1)
    this.#loadVisible().then(() => this.render())
  }

  // --- toolbar actions ----------------------------------------------------
  open() { this.#apply("open") }
  block() { this.#apply("block") }
  clear() { this.#apply("clear") }

  async #apply(mode) {
    if (!this.selStart || !this.selEnd) { this.#msg("Drag to select some dates first.", true); return }
    const [start, end] = [this.selStart, this.selEnd].sort()
    const body = new URLSearchParams({ start_date: start, end_date: end, mode })
    if (mode === "open") {
      if (this.hasPriceTarget && this.priceTarget.value) body.set("price_per_day", this.priceTarget.value)
      if (this.hasMinStayTarget && this.minStayTarget.value) body.set("min_stay_nights", this.minStayTarget.value)
      if (this.hasMaxStayTarget && this.maxStayTarget.value) body.set("max_stay_nights", this.maxStayTarget.value)
      if (this.areaValue && this.hasCapacityTarget && this.capacityTarget.value) {
        body.set("concurrent_hunt_capacity", this.capacityTarget.value)
      }
      // Day-of-week recurrence: only send when some (but not all) days are picked.
      const days = this.dowTargets.filter((c) => c.checked).map((c) => c.value)
      if (days.length && days.length < 7) days.forEach((d) => body.append("days_of_week[]", d))
    }

    try {
      const res = await fetch(this.applyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json",
        },
        body: body.toString(),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) { this.#msg(data.error || "Couldn't save that change.", true); return }

      this.selStart = this.selEnd = null
      this.days = {}
      this.fetched = new Set()
      await this.#loadVisible()
      this.render()
      this.#msg("Saved.")
    } catch (_e) {
      this.#msg("Network error — nothing was changed.", true)
    }
  }

  // --- selection ----------------------------------------------------------
  #onDown = (event) => {
    const cell = event.target.closest(".wl-cal-day[data-date]")
    if (!cell) return
    event.preventDefault()
    this.dragging = true
    this.selStart = this.selEnd = cell.dataset.date
    this.#paintSelection()
    this.#renderSummary()
  }

  #onOver = (event) => {
    if (!this.dragging) return
    const cell = event.target.closest(".wl-cal-day[data-date]")
    if (!cell) return
    this.selEnd = cell.dataset.date
    this.#paintSelection()
    this.#renderSummary()
  }

  #onUp = () => { this.dragging = false }

  #paintSelection() {
    const [s, e] = this.selStart && this.selEnd ? [this.selStart, this.selEnd].sort() : [null, null]
    this.gridTarget.querySelectorAll(".wl-cal-day[data-date]").forEach((cell) => {
      const d = cell.dataset.date
      cell.classList.toggle("is-selected", !!s && d >= s && d <= e)
    })
  }

  // --- rendering ----------------------------------------------------------
  render() {
    if (!this.hasGridTarget) return
    this.gridTarget.innerHTML = this.#monthsShown().map((m) => this.#monthHtml(m)).join("")

    if (this.hasMonthLabelTarget) {
      const a = this.#monthsShown()[0]
      const b = this.#monthsShown().at(-1)
      this.monthLabelTarget.textContent =
        a.getMonth() !== b.getMonth() || a.getFullYear() !== b.getFullYear()
          ? `${MONTH_NAMES[a.getMonth()].slice(0, 3)} – ${MONTH_NAMES[b.getMonth()].slice(0, 3)} ${b.getFullYear()}`
          : `${MONTH_NAMES[a.getMonth()]} ${a.getFullYear()}`
    }
    if (this.hasPrevTarget) this.prevTarget.disabled = this.viewMonth <= this.#startOfMonth(new Date())
    this.#paintSelection()
  }

  #monthHtml(monthDate) {
    const y = monthDate.getFullYear()
    const mo = monthDate.getMonth()
    const firstWeekday = new Date(y, mo, 1).getDay()
    const daysInMonth = new Date(y, mo + 1, 0).getDate()

    const dow = DOW.map((d) => `<span>${d}</span>`).join("")
    let cells = ""
    for (let i = 0; i < firstWeekday; i++) cells += `<div class="wl-cal-day empty"></div>`
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = this.#iso(new Date(y, mo, d))
      const status = this.days[iso] ? this.days[iso].s : "closed"
      cells += `<div class="wl-cal-day is-${status}" data-date="${iso}" role="button" tabindex="0">${d}</div>`
    }

    return `
      <div class="wl-cal-month">
        <div class="wl-cal-month-name">${MONTH_NAMES[mo]} ${y}</div>
        <div class="wl-cal-dow">${dow}</div>
        <div class="wl-cal-days">${cells}</div>
      </div>`
  }

  #renderSummary() {
    if (!this.hasSummaryTarget) return
    if (!this.selStart || !this.selEnd) { this.summaryTarget.textContent = "Drag across the calendar to select dates."; return }
    const [s, e] = [this.selStart, this.selEnd].sort()
    const nights = this.#nightsInclusive(s, e)
    this.summaryTarget.innerHTML = `<strong>${this.#human(s)} – ${this.#human(e)}</strong> · ${nights} night${nights === 1 ? "" : "s"}`
  }

  #msg(text, warn = false) {
    if (this.hasSummaryTarget) {
      this.summaryTarget.innerHTML = `<span class="${warn ? "text-danger" : "text-success"}">${text}</span>`
    }
  }

  // --- data loading -------------------------------------------------------
  #monthsShown() {
    return Array.from({ length: this.monthsValue }, (_, i) => this.#addMonths(this.viewMonth, i))
  }

  async #loadVisible() {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("start", this.#iso(this.#monthsShown()[0]))
      url.searchParams.set("months", String(this.monthsValue))
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()
      Object.assign(this.days, data.days || {})
      this.#monthsShown().forEach((m) => this.fetched.add(this.#monthKey(m)))
    } catch (_e) {
      // Network hiccup — render what we have.
    }
  }

  // --- date helpers (local-time) ------------------------------------------
  #nightsInclusive(startISO, endISO) {
    return this.#daysBetween(startISO, endISO) + 1
  }

  #daysBetween(a, b) {
    return Math.round((this.#parse(b) - this.#parse(a)) / 86_400_000)
  }

  #parse(iso) {
    const [y, m, d] = iso.split("-").map(Number)
    return new Date(y, m - 1, d)
  }

  #iso(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  #human(iso) {
    const d = this.#parse(iso)
    return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`
  }

  #addMonths(date, n) {
    return new Date(date.getFullYear(), date.getMonth() + n, 1)
  }

  #startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  #monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
  }
}
